import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import { getGetAvailabilityQueryKey } from '../../api/generated/endpoints/availability/availability'
import {
  getListMyBookingsQueryKey,
  useCancelBooking,
  useListMyBookings,
} from '../../api/generated/endpoints/bookings/bookings'
import type { BookingRead } from '../../api/generated/model'
import {
  BOOKING_TABS,
  CUSTOMER_REASONS,
  REASON_LABELS,
  STATUS_LABELS,
  TAB_LABELS,
  isBookingTab,
  partitionBookings,
} from '../../lib/bookings'
import type { BookingTab } from '../../lib/bookings'
import { formatUYU } from '../../lib/money'
import { formatDayLong, todayAtFacility } from '../../lib/time'
import { CreditBalance } from '../../components/CreditBalance'
import { OrphanedPaymentsNotice } from '../../components/OrphanedPaymentsNotice'

export const Route = createFileRoute('/_authed/bookings/')({
  // In the URL so a tab survives a reload and can be linked to, the same way
  // /book carries its date.
  validateSearch: (search: Record<string, unknown>): { tab: BookingTab } => ({
    tab: isBookingTab(search.tab) ? search.tab : 'upcoming',
  }),
  component: MyBookingsPage,
})

const EMPTY_STATES: Record<BookingTab, { title: string; body: string }> = {
  upcoming: { title: 'Nada próximo', body: 'Al reservar un simulador aparece acá.' },
  cancelled: {
    title: 'Nada cancelado',
    body: 'Las reservas canceladas aparecen acá, junto con el motivo.',
  },
  past: {
    title: 'Todavía no hay sesiones',
    body: 'Después de jugar, las reservas anteriores quedan listadas acá.',
  },
}

function MyBookingsPage() {
  const { tab } = Route.useSearch()
  const bookings = useListMyBookings()

  if (bookings.isPending) return <p className="text-slate-600">Cargando…</p>
  if (bookings.isError) {
    return (
      <p role="alert" className="text-red-600">
        No se pudieron cargar las reservas.
      </p>
    )
  }

  const grouped = partitionBookings(bookings.data.bookings)
  const shown = grouped[tab]
  const empty = EMPTY_STATES[tab]

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Mis reservas</h1>

      {/* Above the balance because it is the more urgent of the two, and it
          renders nothing at all when there is nothing stranded. */}
      <OrphanedPaymentsNotice />

      <CreditBalance showBuyLink />

      {/* Links, not the ARIA tab pattern: these genuinely navigate, and the
          real pattern would drag in roving focus and JS-swapped panels for no
          gain. aria-current tells a screen reader which one you are on. */}
      <nav className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {BOOKING_TABS.map((candidate) => {
          const isActive = candidate === tab
          const count = grouped[candidate].length
          return (
            <Link
              key={candidate}
              to="/bookings"
              search={{ tab: candidate }}
              aria-current={isActive ? 'page' : undefined}
              className={[
                'flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-4 text-sm',
                isActive
                  ? 'border-slate-900 font-medium text-slate-900'
                  : 'border-transparent text-slate-600 hover:text-slate-900',
              ].join(' ')}
            >
              {TAB_LABELS[candidate]}
              {count > 0 && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs tabular-nums">
                  {count}
                </span>
              )}
            </Link>
          )
        })}
      </nav>

      {shown.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <p className="font-medium">{empty.title}</p>
          <p className="mt-1 text-sm text-slate-600">{empty.body}</p>
          {tab !== 'cancelled' && (
            <Link
              to="/book"
              search={{ date: todayAtFacility() }}
              className="mt-4 inline-flex min-h-11 items-center rounded-md bg-slate-900 px-5 text-white hover:bg-slate-700"
            >
              Reservar un simulador
            </Link>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {shown.map((booking) => (
            <BookingCard key={booking.id} booking={booking} tab={tab} />
          ))}
        </ul>
      )}
    </div>
  )
}

function BookingCard({ booking, tab }: { booking: BookingRead; tab: BookingTab }) {
  const queryClient = useQueryClient()
  const [isCancelling, setIsCancelling] = useState(false)
  const [reason, setReason] = useState<string>('')
  const [note, setNote] = useState('')

  const cancel = useCancelBooking({
    mutation: {
      onSuccess: async () => {
        setIsCancelling(false)
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getListMyBookingsQueryKey() }),
          // The slot is free again, so no availability view may keep showing it
          // as taken.
          queryClient.invalidateQueries({
            queryKey: getGetAvailabilityQueryKey({ date: booking.date }),
          }),
        ])
      },
    },
  })

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium tabular-nums">
            {booking.start} &rarr; {booking.end}
          </p>
          <p className="text-sm text-slate-600">{formatDayLong(booking.date)}</p>
          <p className="text-sm text-slate-600">
            {booking.bay_name} &middot; {formatUYU(booking.price_uyu_pesos)}
          </p>
        </div>
        <span
          className={[
            'shrink-0 rounded-full px-3 py-1 text-xs font-medium',
            booking.status === 'confirmed'
              ? 'bg-green-100 text-green-900'
              : tab === 'upcoming'
                ? 'bg-amber-100 text-amber-900'
                : 'bg-slate-100 text-slate-600',
          ].join(' ')}
        >
          {STATUS_LABELS[booking.status] ?? booking.status}
        </span>
      </div>

      {tab === 'cancelled' && (booking.cancellation_reason || booking.cancellation_note) && (
        <p className="mt-3 border-l-2 border-slate-200 pl-3 text-sm text-slate-600">
          {booking.cancellation_reason && (
            <span className="font-medium text-slate-700">
              {REASON_LABELS[booking.cancellation_reason] ?? booking.cancellation_reason}
            </span>
          )}
          {booking.cancellation_note && (
            <>
              {booking.cancellation_reason && ' — '}
              {booking.cancellation_note}
            </>
          )}
        </p>
      )}

      {tab === 'past' && (
        // Where GSPro session data will live once the simulator is tied in.
        <p className="mt-3 text-sm text-slate-500">Todavía no hay datos de la sesión.</p>
      )}

      {tab === 'upcoming' && !isCancelling && (
        <button
          onClick={() => setIsCancelling(true)}
          className="mt-3 min-h-11 rounded-md border border-slate-300 px-4 text-sm hover:bg-slate-50"
        >
          Cancelar la reserva
        </button>
      )}

      {isCancelling && (
        // Inline rather than a modal: it is a four-option choice, and a dialog
        // would mean building a focus trap and an escape handler for it.
        <div className="mt-4 border-t border-slate-200 pt-4">
          <fieldset>
            <legend className="text-sm font-medium">¿Por qué se cancela?</legend>
            <p className="mt-0.5 text-xs text-slate-500">
              Es opcional; ayuda a organizar la agenda.
            </p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {CUSTOMER_REASONS.map((candidate) => (
                <label key={candidate}>
                  {/* Same visually-hidden radio pattern as the time picker:
                      arrow-key navigation and one tab stop, for free. */}
                  <input
                    type="radio"
                    name={`reason-${booking.id}`}
                    value={candidate}
                    checked={reason === candidate}
                    onChange={() => setReason(candidate)}
                    className="peer sr-only"
                  />
                  <span className="flex min-h-11 cursor-pointer items-center rounded-md border border-slate-300 px-3 text-sm peer-not-checked:hover:bg-slate-50 peer-checked:border-slate-900 peer-checked:bg-slate-900 peer-checked:text-white peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-slate-900">
                    {REASON_LABELS[candidate]}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <label htmlFor={`note-${booking.id}`} className="mt-3 block text-sm font-medium">
            ¿Algo para agregar? <span className="font-normal text-slate-500">(opcional)</span>
          </label>
          <input
            id={`note-${booking.id}`}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={500}
            className="mt-1 min-h-11 w-full rounded-md border border-slate-300 px-3 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          />

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setIsCancelling(false)}
              className="min-h-11 flex-1 rounded-md border border-slate-300 px-4 text-sm hover:bg-slate-50"
            >
              Mantener la reserva
            </button>
            <button
              onClick={() =>
                cancel.mutate({
                  bookingId: booking.id,
                  data: { reason: reason || null, note: note || null },
                })
              }
              disabled={cancel.isPending}
              className="min-h-11 flex-1 rounded-md bg-red-700 px-4 text-sm text-white hover:bg-red-800 disabled:opacity-50"
            >
              {cancel.isPending ? 'Cancelando…' : 'Cancelar la reserva'}
            </button>
          </div>

          {cancel.isError && (
            <p role="alert" className="mt-2 text-sm text-red-600">
              No se pudo cancelar. Intentar de nuevo.
            </p>
          )}
        </div>
      )}
    </li>
  )
}
